import { useState } from 'react';
import { Label } from '@/components/design-system/Label';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/components/design-system/radio-group';
import { Button } from '@/components/design-system/Button';
import { Calendar } from '@/components/design-system/Calendar';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/design-system/Form';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/design-system/popover';
import { cn } from '@/utils/styles';
import { format } from 'date-fns';
import { RegistrationScreen } from '@/components/utils/RegistrationScreen';
import { TextField } from '@design-system/DataForm/TextField';
import usePersonalInfo from '@/hooks/registration/usePersonalInfo';
import { RegistrationTrack } from '@/hooks/useRegistrationStore';
import * as P from '@/constants/paths';

export const RegistrationPersonalInformation = () => {
  const { form, onSubmit } = usePersonalInfo();
  const [popoverOpen, setPopoverOpen] = useState(false);

  return (
    <RegistrationScreen
      form={form}
      title="Personal Information"
      description="Please provide accurate information of the person who will be taking
          the test. We may use information below for contact purpose."
      content={
        <div className="mt-8">
          <TextField
            name="firstName"
            placeholder="First name"
            className="mt-5"
            autoComplete="off"
          />
          <TextField
            name="lastName"
            placeholder="Last name"
            className="mt-5"
            autoComplete="off"
          />
          <FormField
            control={form.control}
            name="birthDay"
            render={({ field }) => (
              <FormItem className="flex flex-col mt-5 mb-5">
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={'outline'}
                        className={cn(
                          'w-full h-[68px] pl-3 text-left font-normal justify-start bg-overlay-primary',
                          !field.value && 'text-muted-foreground',
                        )}
                      >
                        <div className="flex flex-col items-start w-full">
                          <span className="text-sm font-medium text-tertiary">
                            Birthday
                          </span>
                          {field.value instanceof Date &&
                          !isNaN(field.value.getTime()) ? (
                            <span>{format(field.value, 'PPP')}</span>
                          ) : (
                            <div className="flex flex-row justify-between text-secondary-foreground mt-2">
                              <span>MM/DD/YYYY</span>
                            </div>
                          )}
                        </div>
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="p-0 w-auto"
                    sideOffset={8}
                    avoidCollisions={true}
                    collisionPadding={10}
                    side="bottom"
                  >
                    <Calendar
                      mode="single"
                      captionLayout="dropdown"
                      selected={field.value || undefined}
                      onSelect={(date) => {
                        field.onChange(date ?? null);
                        setPopoverOpen(false);
                      }}
                      disabled={(date) =>
                        date > new Date() || date < new Date('1900-01-01')
                      }
                      initialFocus
                      fromYear={1925}
                      toYear={2025}
                      classNames={{
                        caption:
                          'flex justify-center relative items-center pt-0',
                        cell: 'relative text-center text-sm focus-within:relative focus-within:z-20 rounded-r-md p-1',
                      }}
                      className="p-0"
                      showOutsideDays={false}
                      toMonth={new Date()}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          <Label className="mt-5">Biological Sex</Label>
          <FormField
            control={form.control}
            name="gender"
            render={({ field }) => (
              <FormItem className="flex flex-col mt-5 mb-5">
                <FormControl>
                  <RadioGroup
                    defaultValue={field.value}
                    onValueChange={field.onChange}
                    name="gender"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Male" id="male" />
                      <Label htmlFor="male">Male</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Female" id="female" />
                      <Label htmlFor="female">Female</Label>
                    </div>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      }
      previousPath={P.REGISTRATION_ACCOUNT_CREATION}
      primaryActionLabel="Next"
      primaryActionIsSubmit
      formOnValid={onSubmit}
      checkForRegisterStore={RegistrationTrack.PersonalInformation}
    />
  );
};
